<template>
  <div>
    <filter-component
      ref="filterComponent"
      :showFilters="true"
      :hideStatus="true"
      :hideShowDeleted="true"
      :hideExportButton="true"
      @submitFilter="filterSubmitted"
      @resetFilter="resetFilter"
    >
    </filter-component>

    <div v-if="loading" class="text-center my-5">
      <b-spinner label="Loading..."></b-spinner>
    </div>

    <b-row v-else-if="graphs.length">
      <b-col
        md="6"
        lg="6"
        class="mb-2"
        v-for="graph in graphs"
        :key="graph.key"
      >
        <b-card :header="graph.title" header-tag="h5" class="dashboard-graph-card h-100 mb-0">
          <b-card-text class="chart-card-body">
            <apexchart
              v-if="hasChartData(graph)"
              :type="graph.type || 'bar'"
              height="350"
              :options="graph.options"
              :series="graph.series"
            ></apexchart>

            <div v-else class="graph-empty-state">
              <div class="empty-state-icon-box mb-1">
                <feather-icon icon="BarChart2Icon" size="26" />
              </div>
              <h6 class="empty-state-title mb-50">No data found</h6>
              <p class="empty-state-subtitle text-muted mb-0">
                {{
                  hasActiveFilters
                    ? "No entries match the selected filters. Make some entries or try adjusting filters to see results."
                    : "No data found, make some entries to see results."
                }}
              </p>
              <b-button
                v-if="hasActiveFilters"
                variant="outline-primary"
                size="sm"
                class="mt-1 btnBorder"
                @click="resetAllFilters"
              >
                <feather-icon icon="RotateCcwIcon" size="12" class="mr-50" />
                Reset Filters
              </b-button>
            </div>
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>

    <b-card v-else class="text-center my-3 py-3">
      <div class="empty-state-icon-box mx-auto mb-1">
        <feather-icon icon="ShieldIcon" size="26" />
      </div>
      <h6 class="empty-state-title mb-50">No Graphs Configured</h6>
      <p class="text-muted mb-0" style="font-size: 0.875rem;">
        No dashboard graphs are configured for your role.
      </p>
    </b-card>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BCardText, BSpinner, BButton } from "bootstrap-vue";
import FilterComponent from "../../components/Filters.vue";
import apexchart from "vue-apexcharts";
import * as DashBoardServices from "@/apiServices/DashboardServices";


const CURRENCY_COLORS = {
  CHF: "#20306C",
  EUR: "#16DBCC",
  USD: "#FDE68A",
  GBP: "#7367F0",
  Unspecified: "#B9B9C3",
};
const FALLBACK_COLORS = ["#20306C", "#16DBCC", "#FF9F43", "#7367F0", "#EA5455", "#00CFE8"];


const DRILL_TARGETS = {
  milestones_sales: { path: "/milestones", query: { contract_nature: "Sales" } },
  milestones_acquisitions: {
    path: "/milestones",
    query: { contract_nature: "Acquisition" },
  },
  total_expenses: { path: "/addrevenueMain", query: { tabIndex: 3 } },
  total_revenue: { path: "/addrevenueMain", query: { tabIndex: 1 } },
  theatrical_revenue: {
    path: "/addrevenueMain",
    query: { tabIndex: 1, rightType: 1 },
  },
  home_entertainment_revenue: {
    path: "/addrevenueMain",
    query: { tabIndex: 1 },
    useRightIds: true,
  },
  demand_revenue: {
    path: "/addrevenueMain",
    query: { tabIndex: 1 },
    useRightIds: true,
  },
  pay_tv_revenue: {
    path: "/addrevenueMain",
    query: { tabIndex: 1 },
    useRightIds: true,
  },
  free_tv_revenue: {
    path: "/addrevenueMain",
    query: { tabIndex: 1 },
    useRightIds: true,
  },
  cable_retransmission_revenue: {
    path: "/addrevenueMain",
    query: { tabIndex: 1 },
    useRightIds: true,
  },
};
const DEFAULT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BCardText,
    BSpinner,
    BButton,
    apexchart,
    FilterComponent,
  },

  data() {
    return {
      graphs: [],
      months: [...DEFAULT_MONTHS],
      role: "",
      year: null,
      loading: false,
      filters: {},
    };
  },

  computed: {
    hasActiveFilters() {
      const f = this.filters || {};
      return Boolean(
        f.movie ||
        (f.vendor && (f.vendor.vendor_id || f.vendor.id || f.vendor)) ||
        f.date_range ||
        (f.language && (f.language.lbl_id || f.language.id || f.language)) ||
        (f.right && (f.right.lbl_id || f.right.id || f.right))
      );
    },
  },

  beforeMount() {
    this.getGraphs();
  },

  methods: {
    hasChartData(graph) {
      if (!graph) return false;
      if (typeof graph.has_data === "boolean") {
        return graph.has_data;
      }
      if (!graph.series || !Array.isArray(graph.series) || !graph.series.length) {
        return false;
      }
      return graph.series.some((s) => {
        if (!s || !Array.isArray(s.data) || !s.data.length) return false;
        return s.data.some((val) => {
          const num = Number(val);
          return !Number.isNaN(num) && num !== 0;
        });
      });
    },

    async getGraphs() {
      this.loading = true;
      try {
        const response = await DashBoardServices.GetRoleGraphs(this.buildPayload());
        if (response.data.status) {
          const data = response.data.data || {};
          this.role = data.role || "";
          this.year = data.year || null;
          this.months =
            data.months && data.months.length ? data.months : [...DEFAULT_MONTHS];
          this.graphs = (data.graphs || []).map((graph) => ({
            ...graph,
            options: this.buildChartOptions(graph),
          }));
        } else {
          this.graphs = [];
        }
      } catch (err) {
        console.log("Error in getting dashboard graphs ", err);
        this.graphs = [];
      } finally {
        this.loading = false;
      }
    },

    buildPayload() {
      const f = this.filters || {};
      return {
        movie_id: f.movie ? f.movie : "",
        vendor_id: f.vendor ? f.vendor.vendor_id : "",
        start_date: f.date_range ? f.date_range.split(" to ")[0] : "",
        end_date: f.date_range ? f.date_range.split(" to ")[1] : "",
        language: f.language ? f.language.lbl_id : "",
        right: f.right ? f.right.lbl_id : "",
      };
    },

    buildChartOptions(graph) {
      const formatNumber = (value) => {
        const num = Number(value);
        if (Number.isNaN(num)) return value;
        return num.toLocaleString();
      };

      const seriesList = graph.series || [];
      const colors = seriesList.map(
        (s, i) => CURRENCY_COLORS[s.name] || FALLBACK_COLORS[i % FALLBACK_COLORS.length]
      );
      const showLegend = seriesList.length > 1;
      const drill = DRILL_TARGETS[graph.key];

      return {
        chart: {
          type: graph.type || "bar",
          height: 350,
          stacked: true,
          toolbar: { show: true },
          zoom: { enabled: true },
          events: drill
            ? {
                dataPointSelection: (event, chartContext, config) => {
                  this.drillToPage(graph, config.dataPointIndex);
                },
              }
            : {},
        },
        colors,
        dataLabels: { enabled: false },
        legend: {
          show: showLegend,
          position: "bottom",
          horizontalAlign: "center",
          fontSize: "12px",
          markers: { radius: 3 },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 10,
            dataLabels: {
              total: {
                enabled: true,
                formatter: formatNumber,
                style: { fontSize: "13px", fontWeight: 900 },
              },
            },
          },
        },
        xaxis: { categories: this.months },
        yaxis: {
          min: (min) => (min < 0 ? min : 0),
          forceNiceScale: true,
          labels: { formatter: formatNumber },
        },
        noData: {
          text: "No data found, make some entries to see results",
          align: "center",
          verticalAlign: "middle",
          style: {
            color: "#82868b",
            fontSize: "13px",
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({ series, dataPointIndex, w }) => {
            const month = (w.globals.labels && w.globals.labels[dataPointIndex]) || "";
            const names = w.globals.seriesNames || [];
            const cols = w.globals.colors || [];
            let rows = "";
            let total = 0;
            let nonZero = 0;
            for (let i = 0; i < series.length; i += 1) {
              const val = Number(series[i][dataPointIndex]) || 0;
              total += val;
              if (val === 0) continue;
              nonZero += 1;
              rows +=
                '<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:2px 0;">' +
                '<span style="display:flex;align-items:center;gap:6px;">' +
                '<span style="width:9px;height:9px;border-radius:2px;display:inline-block;background:' +
                (cols[i] || "#999") +
                ';"></span>' +
                '<span style="color:#6e6b7b;">' +
                (names[i] || "") +
                "</span></span>" +
                '<span style="font-weight:600;color:#5e5873;">' +
                formatNumber(val) +
                "</span></div>";
            }
            if (!rows) {
              rows = '<div style="padding:2px 0;color:#b9b9c3;">No data</div>';
            }
            const totalRow =
              nonZero > 1
                ? '<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;border-top:1px solid #ebe9f1;margin-top:4px;padding-top:4px;font-weight:700;color:#5e5873;">' +
                  "<span>Total</span><span>" +
                  formatNumber(total) +
                  "</span></div>"
                : "";
            return (
              '<div style="padding:8px 10px;font-size:12px;min-width:160px;">' +
              '<div style="font-weight:700;color:#5e5873;margin-bottom:4px;">' +
              month +
              "</div>" +
              rows +
              totalRow +
              "</div>"
            );
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: { position: "bottom", offsetX: -10, offsetY: 0 },
            },
          },
        ],
        fill: { opacity: 1 },
      };
    },

    drillToPage(graph, monthIndex) {
      const target = DRILL_TARGETS[graph.key];
      if (!target || monthIndex === undefined || monthIndex === null || monthIndex < 0) {
        return;
      }
      const { start, end } = this.monthRange(monthIndex);
      const query = { ...target.query, start_date: start, end_date: end };
      if (target.useRightIds && graph.right_ids && graph.right_ids.length) {
        query.subRights = graph.right_ids.join(",");
      }
      this.$router.push({ path: target.path, query }).catch(() => {});
    },

    monthRange(monthIndex) {
      const year = this.year || new Date().getFullYear();
      const mm = String(monthIndex + 1).padStart(2, "0");
      const lastDay = new Date(year, monthIndex + 1, 0).getDate();
      return {
        start: `${year}-${mm}-01`,
        end: `${year}-${mm}-${String(lastDay).padStart(2, "0")}`,
      };
    },

    filterSubmitted(filters) {
      this.filters = filters;
      this.onChange();
    },

    onChange() {
      this.$nextTick(() => {
        this.getGraphs();
      });
    },

    resetFilter() {
      this.filters = {};
      this.onChange();
    },

    resetAllFilters() {
      if (
        this.$refs.filterComponent &&
        typeof this.$refs.filterComponent.resetForm === "function"
      ) {
        this.$refs.filterComponent.resetForm();
      } else {
        this.resetFilter();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard-graph-card {
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 18px 0 rgba(34, 41, 47, 0.08);
  }
}

.chart-card-body {
  min-height: 350px;
}

.graph-empty-state {
  height: 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
  background-color: #fafbfe;
  border-radius: 8px;
  border: 1px dashed #d8d6de;

  .empty-state-icon-box {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: rgba(32, 48, 108, 0.08);
    color: #20306c;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
  }

  &:hover .empty-state-icon-box {
    transform: scale(1.08);
  }

  .empty-state-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #4b4b5a;
  }

  .empty-state-subtitle {
    font-size: 0.84rem;
    max-width: 290px;
    line-height: 1.45;
    color: #82868b !important;
  }

  .btnBorder {
    border-radius: 8px;
  }
}

.empty-state-icon-box {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(32, 48, 108, 0.08);
  color: #20306c;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

